/**
 * @fileoverview A function for fetching GBP locations associated with a given GBP account.
 *
 * @author Kuber Sethi (ksethi@yext.com)
 */
import Uri from 'goog:goog.Uri';

import {Routes} from '/src/com/yext/listingslaunchstorm/js/routes';

import {useAjax} from '/src/com/yext/listingslaunchstorm/js/common/hooks/useajax';
import {doRequest} from '/src/com/yext/listingslaunchstorm/js/common/util/dorequest';
import {getTestCases} from '/src/com/yext/listingslaunchstorm/js/common/util/gettestcases';
import {mockRequest} from '/src/com/yext/listingslaunchstorm/js/common/util/mockrequest';
import {GbpGroup} from '/src/com/yext/listingslaunchstorm/js/enterprise/setuppublisher/data/gbpgroup';
import {GetGbpGroupLocationsResponse} from '/src/com/yext/listingslaunchstorm/js/enterprise/setuppublisher/data/getgbpgrouplocationsresponse';
import {mockGetGbpGroupLocationsResponse} from '/src/com/yext/listingslaunchstorm/js/enterprise/setuppublisher/mocks/mocks';

/**
 * @param {number} linkedAccountId
 * @param {Array<GbpGroup> | undefined} gbpGroups
 * @param {string=} testCase
 * @return {{
 *   status: (string | undefined),
 *   data: (GetGbpGroupLocationsResponse | undefined),
 *   refetch: function(),
 *   abort: function(),
 * }}
 */
export function useGbpGroupLocations(linkedAccountId, gbpGroups, testCase='') {
  const businessId = window['businessId'];
  return useAjax(gbpGroups?.length > 0, fetchGbpGroupLocations, businessId, linkedAccountId, gbpGroups, testCase);
}

const testCases = getTestCases(mockGetGbpGroupLocationsResponse);

/**
 * @param {number} businessId
 * @param {number} linkedAccountId
 * @param {Array<GbpGroup>} gbpGroups
 * @param {string} testCase
 * @param {AbortSignal} abortSignal
 * @return {!Promise<GetGbpGroupLocationsResponse>}
 */
function fetchGbpGroupLocations(businessId, linkedAccountId, gbpGroups, testCase, abortSignal) {
  const gbpGroupIds = gbpGroups?.map(gbpGroup => gbpGroup.id) ?? [];
  if (testCases.has(testCase)) {
    console.log(`mock fetchGbpGroupLocations: businessId ${businessId} linkedAccountId ${linkedAccountId} testCase ${testCase}`);
    return mockRequest(abortSignal, testCases.get(testCase));
  }

  const query = new Uri.QueryData;
  query.set('externalAccountId', linkedAccountId);
  query.set('gbpGroupIds', gbpGroupIds);

  const uri = new Uri(Routes.getGbpGroupLocations()).setQueryData(query);

  return doRequest('fetchGbpGroupLocations', abortSignal, uri, 'GET')
    .then(jsonResponse => GetGbpGroupLocationsResponse.fromJSON(jsonResponse));
}
